import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import { Users as UsersIcon, Plus, Edit2, ShieldCheck, Eye, PenLine, AlertOctagon, RotateCcw, Lock, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { apiError } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';
import Modal from '../components/common/Modal';

const ROLES = ['admin', 'manager', 'sales_manager', 'production_manager'];
const ROLE_BADGE = { admin: 'badge-red', manager: 'badge-orange', sales_manager: 'badge-blue', production_manager: 'badge-green' };
const GROUP_ICON = { view: Eye, edit: PenLine, sensitive: AlertOctagon };
const pk = k => 'perm_' + k.replace('.', '_');

/** Grouped checkboxes of every permission. `locked` shows the list read-only (role defaults / admin). */
function PermissionGrid({ catalog, value, onChange, locked, t }) {
  const groups = useMemo(() => ['view', 'edit', 'sensitive'].map(g => ({ g, keys: catalog.permissions.filter(p => p.group === g).map(p => p.key) })), [catalog]);
  const set = new Set(value || []);
  const toggle = k => {
    if (locked) return;
    const next = new Set(set);
    if (next.has(k)) {
      next.delete(k);
      Object.entries(catalog.requires).forEach(([a, b]) => { if (b === k) next.delete(a); });   // no "edit" without "view"
    } else {
      next.add(k);
      if (catalog.requires[k]) next.add(catalog.requires[k]);
    }
    onChange(catalog.permissions.map(p => p.key).filter(x => next.has(x)));
  };
  const setGroup = (keys, on) => {
    if (locked) return;
    const next = new Set(set);
    keys.forEach(k => { if (on) { next.add(k); if (catalog.requires[k]) next.add(catalog.requires[k]); } else next.delete(k); });
    if (!on) Object.entries(catalog.requires).forEach(([a, b]) => { if (!next.has(b)) next.delete(a); });
    onChange(catalog.permissions.map(p => p.key).filter(x => next.has(x)));
  };
  return (
    <div className={`pg ${locked ? 'locked' : ''}`}>
      {groups.map(({ g, keys }) => {
        const Icon = GROUP_ICON[g];
        const all = keys.every(k => set.has(k));
        return (
          <div key={g} className={`pg-group pg-${g}`}>
            <div className="pg-head"><Icon size={14} /> <b>{t('perm_group_' + g)}</b>
              {!locked && <button type="button" onClick={() => setGroup(keys, !all)}>{all ? t('perm_none') : t('perm_all')}</button>}</div>
            <div className="pg-items">{keys.map(k => (
              <label key={k} className={set.has(k) ? 'on' : ''} title={t(pk(k) + '_hint', '')}>
                <input type="checkbox" checked={set.has(k)} disabled={locked} onChange={() => toggle(k)} />
                <span>{t(pk(k))}</span>
              </label>
            ))}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function Users() {
  const { t } = useTranslation();
  const { user: me, can } = useAuthStore();
  const isAdmin = can('users.manage');
  const [users, setUsers] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [tab, setTab] = useState('users');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [roleEdit, setRoleEdit] = useState({});   // role -> edited list

  const load = () => Promise.all([authAPI.listUsers(), authAPI.permissions()])
    .then(([u, c]) => { setUsers(u.data); setCatalog(c.data); })
    .catch(() => { toast.error(t('error')); setUsers([]); });
  useEffect(() => { load(); }, []); // eslint-disable-line

  const roleDefaults = r => (r === 'admin' ? catalog.permissions.map(p => p.key) : (catalog.roles.find(x => x.role === r)?.permissions || []));
  const openCreate = () => setForm({ username: '', password: '', full_name: '', role: 'sales_manager', is_active: true, custom: false, permissions: roleDefaults('sales_manager') });
  const openEdit = u => setForm({ id: u.id, username: u.username, password: '', full_name: u.full_name, role: u.role, is_active: u.is_active,
    custom: !!u.custom, permissions: u.permissions, wasCustom: !!u.custom });
  const setRole = role => setForm(f => ({ ...f, role, permissions: f.custom && role !== 'admin' ? f.permissions : roleDefaults(role), custom: role === 'admin' ? false : f.custom }));

  const save = async () => {
    if (!form.full_name.trim() || (!form.id && !form.username.trim())) return toast.error(t('fill_required'));
    if (!form.id && (form.password || '').length < 6) return toast.error(t('pwd_too_short'));
    if (form.password && form.password.length < 6) return toast.error(t('pwd_too_short'));
    setSaving(true);
    try {
      if (form.id) {
        const data = { full_name: form.full_name, role: form.role, is_active: form.is_active };
        if (form.password) data.password = form.password;
        if (form.custom) data.permissions = form.permissions; else data.use_role_defaults = true;
        await authAPI.updateUser(form.id, data);
      } else {
        await authAPI.createUser({ username: form.username.trim(), full_name: form.full_name, password: form.password, role: form.role,
          permissions: form.custom ? form.permissions : null });
      }
      toast.success(t('saved')); setForm(null); load();
      if (form.id === me?.id) authAPI.me().then(r => useAuthStore.getState().setUser(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  const saveRole = async (role, perms) => {
    try { const r = await authAPI.updateRole(role, perms); setCatalog(r.data); setRoleEdit(x => { const n = { ...x }; delete n[role]; return n; }); toast.success(t('saved')); load(); }
    catch (e) { toast.error(apiError(e, t)); }
  };

  if (!users || !catalog) return <div className="loading-center"><div className="spinner" /></div>;
  const count = u => (u.role === 'admin' ? catalog.permissions.length : u.permissions.length);

  return (
    <div className="space-y-5">
      <SectionHero title={t('users')} subtitle={<>{users.filter(u => u.is_active).length} {t('active')} · {users.length} {t('users')}</>}>
        {isAdmin && <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> {t('add_user')}</button>}
      </SectionHero>

      <div className="tabs">
        <button className={`tab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}><UsersIcon size={14} /> {t('users')}</button>
        <button className={`tab ${tab === 'roles' ? 'active' : ''}`} onClick={() => setTab('roles')}><ShieldCheck size={14} /> {t('roles_and_perms')}</button>
      </div>

      {tab === 'users' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap"><table>
            <thead><tr><th>{t('full_name')}</th><th>{t('username')}</th><th>{t('role')}</th><th>{t('perm_access')}</th><th>{t('status')}</th>{isAdmin && <th></th>}</tr></thead>
            <tbody>{users.map(u => (
              <tr key={u.id} style={{ cursor: isAdmin ? 'pointer' : 'default' }} onClick={() => isAdmin && openEdit(u)}>
                <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{u.full_name}{u.id === me?.id && <span className="text-xs" style={{ color: 'var(--text-muted)' }}> ({t('you')})</span>}</td>
                <td style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{u.username}</td>
                <td><span className={`badge ${ROLE_BADGE[u.role] || ''}`}>{t('role_' + u.role)}</span></td>
                <td><div className="perm-meter"><i style={{ width: `${(count(u) / catalog.permissions.length) * 100}%` }} /></div>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{count(u)} / {catalog.permissions.length}</span>
                  {u.custom && <span className="badge badge-yellow" style={{ marginInlineStart: 6 }}>{t('perm_custom')}</span>}</td>
                <td>{u.is_active ? <span className="badge badge-green">{t('active')}</span> : <span className="badge badge-red">{t('inactive')}</span>}
                  {u.locked && <span className="badge badge-yellow" title={t('user_locked_hint')} style={{ marginInlineStart: 6 }}>{t('user_locked')}</span>}</td>
                {isAdmin && <td><button className="btn btn-ghost btn-sm btn-icon" onClick={e => { e.stopPropagation(); openEdit(u); }}><Edit2 size={13} /></button></td>}
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      )}

      {tab === 'roles' && (
        <div className="space-y-4">
          <p className="text-xs" style={{ color: 'var(--text-muted)', maxWidth: 760 }}>{t('roles_hint')}</p>
          {ROLES.map(r => {
            const info = catalog.roles.find(x => x.role === r);
            const editing = roleEdit[r];
            const value = editing || roleDefaults(r);
            const nUsers = users.filter(u => u.role === r && !u.custom).length;
            return (
              <div key={r} className="card role-card">
                <div className="role-head">
                  <span className={`badge ${ROLE_BADGE[r]}`}>{t('role_' + r)}</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{nUsers} {t('role_users_following')}</span>
                  {info?.customized && <span className="badge badge-yellow">{t('role_changed')}</span>}
                  <div className="role-actions">
                    {r === 'admin' ? <span className="text-xs" style={{ color: 'var(--text-muted)' }}><Lock size={12} /> {t('role_admin_all')}</span> : <>
                      {editing && <button className="btn btn-primary btn-sm" onClick={() => saveRole(r, editing)}><Check size={13} /> {t('save')}</button>}
                      {editing && <button className="btn btn-ghost btn-sm" onClick={() => setRoleEdit(x => { const n = { ...x }; delete n[r]; return n; })}>{t('cancel')}</button>}
                      {!editing && info?.customized && <button className="btn btn-ghost btn-sm" onClick={() => { if (window.confirm(t('role_reset_confirm'))) saveRole(r, null); }}><RotateCcw size={13} /> {t('role_reset')}</button>}
                    </>}
                  </div>
                </div>
                <PermissionGrid catalog={catalog} value={value} locked={r === 'admin'} t={t} onChange={v => setRoleEdit(x => ({ ...x, [r]: v }))} />
              </div>
            );
          })}
        </div>
      )}

      {form && (
        <Modal title={form.id ? t('edit_user') : t('add_user')} size="modal-lg" onClose={() => setForm(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setForm(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? t('loading') : t('save')}</button></>}>
          <div className="form-row ex-row3">
            <div className="form-group"><label className="form-label">{t('full_name')} *</label><input className="input" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">{t('username')} *</label><input className="input" dir="ltr" value={form.username} disabled={!!form.id} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">{form.id ? t('new_password_optional') : t('password') + ' *'}</label><input className="input" type="password" autoComplete="new-password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('role')}</label>
              <select className="select" value={form.role} onChange={e => setRole(e.target.value)}>{ROLES.map(r => <option key={r} value={r}>{t('role_' + r)}</option>)}</select>
              {form.id && form.role !== users.find(u => u.id === form.id)?.role && <p className="text-xs mt-1" style={{ color: '#fbbf24' }}>{t('role_change_note')}</p>}</div>
            <div className="form-group"><label className="form-label">{t('status')}</label>
              <label className="ex-check" style={{ marginTop: 8 }}><input type="checkbox" checked={form.is_active} disabled={form.id === me?.id} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /> {t('active')}</label></div>
          </div>
          <div className="perm-mode">
            <span className="form-label" style={{ margin: 0 }}>{t('perm_access')}</span>
            <div className="mg-cats">
              <button type="button" className={!form.custom ? 'on' : ''} onClick={() => setForm(f => ({ ...f, custom: false, permissions: roleDefaults(f.role) }))}>{t('perm_follow_role')}</button>
              <button type="button" className={form.custom ? 'on' : ''} disabled={form.role === 'admin'} style={{ '--c': '#fbbf24' }} onClick={() => setForm(f => ({ ...f, custom: true }))}>{t('perm_custom_for_user')}</button>
            </div>
          </div>
          <p className="text-xs" style={{ color: 'var(--text-muted)', margin: '6px 0 10px' }}>{form.role === 'admin' ? t('role_admin_all') : form.custom ? t('perm_custom_hint') : t('perm_follow_hint')}</p>
          <PermissionGrid catalog={catalog} value={form.role === 'admin' ? roleDefaults('admin') : form.permissions} locked={!form.custom || form.role === 'admin'} t={t}
            onChange={v => setForm(f => ({ ...f, permissions: v }))} />
        </Modal>
      )}
    </div>
  );
}
